import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Compass, Loader2, CheckCircle2, HeartHandshake } from 'lucide-react';

const api = axios.create({ baseURL: '/api/sc-public' });

const ROLES = [['self', 'Myself'], ['family', 'Family / carer'], ['provider', 'Another provider'], ['hospital', 'Hospital / health'], ['other', 'Other']];
const URGENCY = [['low', 'Not urgent'], ['medium', 'Soon'], ['high', 'Urgent']];

export default function ScIntakeForm() {
  const { token } = useParams();
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    referrer_name: '', referrer_email: '', referrer_phone: '', referrer_org: '', referrer_role: 'self',
    participant_first: '', participant_last: '', participant_dob: '', participant_ndis: '',
    participant_phone: '', participant_email: '', support_needs: '', urgency: 'medium', preferred_contact: '', consent: false,
  });
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  useEffect(() => {
    api.get(`/intake/${token}`).then((r) => setOrg(r.data)).catch(() => setNotFound(true)).finally(() => setLoading(false));
  }, [token]);

  const brand = org?.brandColor || '#4f46e5';
  const submit = async (e) => {
    e.preventDefault();
    if (!f.participant_first.trim() && !f.participant_last.trim()) return toast.error('Please enter the participant’s name');
    if (!f.support_needs.trim()) return toast.error('Please describe the support needs');
    if (!f.consent) return toast.error('Please confirm consent to be contacted');
    setBusy(true);
    try { await api.post(`/intake/${token}`, f); setDone(true); }
    catch (err) { toast.error(err.response?.data?.message || 'Could not submit — please try again'); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="animate-spin text-slate-300" size={28} /></div>;
  if (notFound) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="text-center max-w-md">
        <Compass className="mx-auto text-slate-300 mb-3" size={40} />
        <h1 className="text-lg font-bold text-slate-700">Referral form unavailable</h1>
        <p className="text-sm text-slate-400 mt-1">This link isn’t active. Please contact the provider directly.</p>
      </div>
    </div>
  );
  if (done) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="text-center max-w-md bg-white rounded-2xl shadow-sm border border-slate-100 p-10">
        <CheckCircle2 className="mx-auto mb-4" size={48} style={{ color: brand }} />
        <h1 className="text-xl font-bold text-slate-800">Referral received</h1>
        <p className="text-sm text-slate-500 mt-2">Thank you. {org?.orgName} has received your referral and will be in touch soon.</p>
      </div>
    </div>
  );

  const label = 'block text-xs font-semibold text-slate-500 mb-1';
  const input = 'w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-slate-400 focus:ring-0 text-sm';
  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          {org?.logo ? <img src={org.logo} alt="" className="h-11 w-11 rounded-xl object-contain bg-white border border-slate-100" />
            : <span className="h-11 w-11 rounded-xl flex items-center justify-center text-white" style={{ background: brand }}><HeartHandshake size={22} /></span>}
          <div>
            <h1 className="text-xl font-bold text-slate-800">{org?.orgName}</h1>
            <p className="text-sm text-slate-400">Support Coordination — referral form</p>
          </div>
        </div>
        <form onSubmit={submit} className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 sm:p-8 space-y-6">
          <p className="text-sm text-slate-500">Refer yourself or someone else for NDIS support coordination. We’ll review your referral and get in touch.</p>

          <div>
            <h2 className="text-sm font-bold text-slate-700 mb-3">Who are you?</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><label className={label}>Your name</label><input className={input} value={f.referrer_name} onChange={set('referrer_name')} /></div>
              <div><label className={label}>You are…</label><select className={input} value={f.referrer_role} onChange={set('referrer_role')}>{ROLES.map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
              <div><label className={label}>Email</label><input type="email" className={input} value={f.referrer_email} onChange={set('referrer_email')} /></div>
              <div><label className={label}>Phone</label><input className={input} value={f.referrer_phone} onChange={set('referrer_phone')} /></div>
              <div className="sm:col-span-2"><label className={label}>Organisation (if referring on behalf)</label><input className={input} value={f.referrer_org} onChange={set('referrer_org')} /></div>
            </div>
          </div>

          <div className="pt-2 border-t border-slate-100">
            <h2 className="text-sm font-bold text-slate-700 mb-3 mt-3">The participant</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><label className={label}>First name</label><input className={input} value={f.participant_first} onChange={set('participant_first')} /></div>
              <div><label className={label}>Last name</label><input className={input} value={f.participant_last} onChange={set('participant_last')} /></div>
              <div><label className={label}>Date of birth</label><input type="date" className={input} value={f.participant_dob} onChange={set('participant_dob')} /></div>
              <div><label className={label}>NDIS number</label><input className={input} value={f.participant_ndis} onChange={set('participant_ndis')} /></div>
              <div><label className={label}>Participant phone</label><input className={input} value={f.participant_phone} onChange={set('participant_phone')} /></div>
              <div><label className={label}>Participant email</label><input type="email" className={input} value={f.participant_email} onChange={set('participant_email')} /></div>
            </div>
          </div>

          <div className="pt-2 border-t border-slate-100">
            <h2 className="text-sm font-bold text-slate-700 mb-3 mt-3">Support needs</h2>
            <div className="space-y-3">
              <div><label className={label}>What support is needed? *</label><textarea rows={4} className={input} value={f.support_needs} onChange={set('support_needs')} placeholder="Describe the participant’s situation, goals and what they’re looking for." /></div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><label className={label}>How urgent?</label><select className={input} value={f.urgency} onChange={set('urgency')}>{URGENCY.map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
                <div><label className={label}>Best way / time to contact</label><input className={input} value={f.preferred_contact} onChange={set('preferred_contact')} placeholder="e.g. phone, weekday mornings" /></div>
              </div>
            </div>
          </div>

          <label className="flex items-start gap-2.5 text-sm cursor-pointer">
            <input type="checkbox" className="mt-0.5 w-4 h-4 rounded" checked={f.consent} onChange={set('consent')} />
            <span className="text-slate-600">I consent to being contacted about this referral, and confirm I have the participant’s permission to share their details.</span>
          </label>

          <button type="submit" disabled={busy} className="w-full py-3 rounded-xl text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: brand }}>
            {busy ? <Loader2 size={17} className="animate-spin" /> : null}{busy ? 'Submitting…' : 'Submit referral'}
          </button>
        </form>
        <p className="text-center text-xs text-slate-300 mt-4">Powered by Rostery</p>
      </div>
    </div>
  );
}
